/**
 * Issue Operator Action — the REPAIR an operator can run from the Issues page.
 *
 * ## Why this exists
 *
 * The Issues page's own mutation is `PATCH /admin-panel/issues/:id/status`, whose
 * three verbs are open, ignore and resolve. Every one of them changes how the
 * ledger DESCRIBES the world and none of them changes the world. Without an
 * action key an operator has to know, unwritten, which admin page holds the lever
 * for each code.
 *
 * So a code declares its repair and the page runs it. The issue row already
 * carries `organizationId` and `entityId`, which is everything a per-tenant repair
 * needs as an argument — the action key is the only missing piece.
 *
 * ## Why a KEY and not a URL
 *
 * `IssueCta` carries an href because a customer's next step is navigation. An
 * operator's next step is a MUTATION, and a mutation cannot be expressed as a
 * link without either leaking a method into an href or inventing a GET that
 * writes. A key lets the admin frontend map to a real typed mutation, and lets
 * `Record<IssueOperatorAction, …>` make an unhandled key a compile error rather
 * than a button that silently does nothing.
 *
 * ## Adding one
 *
 * Add the key here, set `operatorAction` on the code in its registry, then add
 * the handler entry in the admin Issues page's action map. The `Record` makes
 * the third step mandatory — skip it and the frontend does not compile.
 */

export const ISSUE_OPERATOR_ACTION = {
  /**
   * Force the tenant's Vibe cluster to converge — HEALTHY → DEGRADED with an
   * operator reason, which the next reconcile tick drives back to HEALTHY with a
   * full `pulumi up`.
   *
   * The repair for any Vibe alarm whose cause is a declared resource that is
   * missing or wrong on live infrastructure. A converge re-applies the program,
   * so it fixes the resource without touching anything the program still agrees
   * with. `entityId` on the issue is the cluster's `organizationId`.
   */
  VIBE_FORCE_CONVERGE_CLUSTER: "vibe_force_converge_cluster",

  /**
   * Run the tenant's on-demand backup NOW (NEX-3137) — `StartBackupJob` into the
   * regular vault plus, when a completed local point already exists, an
   * independent `StartCopyJob` to the DR vault. The repair for
   * `VIBE_TENANT_BACKUP_STALE`, `VIBE_TENANT_DR_COPY_STALE` and
   * `VIBE_TENANT_BACKUP_JOB_FAILING` — all three take the same lever, because
   * "it did not arrive" and "it arrived broken" are both answered by running it
   * again and watching. `entityId` on the issue is the cluster's `organizationId`
   * (`ISSUE_ENTITY_TYPE.VIBE_CLUSTER`), same as `VIBE_FORCE_CONVERGE_CLUSTER`.
   */
  VIBE_RUN_TENANT_BACKUP_NOW: "vibe_run_tenant_backup_now",

  /**
   * Re-run ONE APP's logical dump today (NEX-3196) — the repair for
   * `VIBE_APP_BACKUP_STALE` and `VIBE_APP_BACKUP_JOB_FAILING`.
   *
   * 🔴 `entityId` HERE IS THE APP'S OWN ID, NOT THE ORGANIZATION'S. Both actions
   * above are `ISSUE_ENTITY_TYPE.VIBE_CLUSTER`-scoped, where `entityId` happens
   * to BE the organizationId — so the convention the remedy modal grew for them
   * (`entityId === "" ? organizationId : entityId`) is not merely unhelpful here,
   * it silently addresses the repair at an app id in the organizationId slot. An
   * app-family action reads `issue.organizationId` directly and passes
   * `issue.entityId` as the app.
   *
   * NOT an AWS Backup call. The app family is an agent/Nomad `pg_dump` pipeline
   * that the control plane cannot reach, so this records a request the tenant's
   * own agent honours on its next poll. See `RequestVibeAppDataRunUseCase`.
   */
  VIBE_RUN_APP_BACKUP_NOW: "vibe_run_app_backup_now",

  /**
   * Re-run ONE APP's restore drill today (NEX-3196) — the repair for
   * `VIBE_APP_RESTORE_DRILL_FAILED` and `VIBE_APP_RESTORE_DRILL_STALE`.
   *
   * Same `VIBE_APP` entity scoping and same agent-dispatched mechanism as
   * `VIBE_RUN_APP_BACKUP_NOW`; read that key's note before wiring either.
   *
   * This one additionally overrides the WEEKLY ROTATION. A drill is normally
   * projected only on the app's own staggered rotation day, so without the
   * override an operator would click on a Tuesday and see nothing happen until
   * the following Sunday.
   */
  VIBE_RUN_APP_RESTORE_DRILL_NOW: "vibe_run_app_restore_drill_now",

  /**
   * Run the tenant's restore DRILL now (NEX-3195) — restore the newest recovery
   * point of the Forgejo git-repo volume into a throwaway EBS volume, launch the
   * validator that mounts it and reads the bare git repositories back, then
   * delete that volume. The repair for `VIBE_TENANT_RESTORE_DRILL_FAILED` and
   * `VIBE_TENANT_RESTORE_DRILL_STALE`.
   *
   * 🔴 THE SIBLING OF `VIBE_RUN_TENANT_BACKUP_NOW`, AND NOT THE SAME SHAPE AT
   * ALL. That one dispatches into vaults AWS already manages under retention
   * nobody has to remember. This one bypasses AWS Backup's restore-testing
   * mechanism entirely — there is no API to trigger a Restore Testing Plan run
   * on demand — so it calls `StartRestoreJob` directly, and AWS's documented
   * "delete the restored resource when validation finishes" behaviour is a
   * property of a restore-testing PLAN execution, which this is not.
   *
   * ⚠️ AWS's cleanup is not absent, only unproven where it matters. Measured on
   * a real manual dispatch: when the restore FAILS, AWS Backup deletes the
   * volume itself. The SUCCESS path — the one this lever exists for — has not
   * been observed, so the control plane still owns the delete.
   *
   * So the button is only half a lever: the other half is the control plane
   * owning that volume's whole life, which is why pressing it starts a
   * background poller rather than doing the work inside the request. See
   * `RunTenantRestoreDrillNowUseCase` and `SettleOperatorRestoreDrillsUseCase`.
   *
   * `entityId` on the issue is the cluster's `organizationId`
   * (`ISSUE_ENTITY_TYPE.VIBE_CLUSTER`), same as the two actions above.
   */
  VIBE_RUN_TENANT_RESTORE_DRILL_NOW: "vibe_run_tenant_restore_drill_now",

  /**
   * Re-enable Total TLS on the shared Cloudflare zone (NEX-3136) — the repair
   * for `VIBE_ZONE_TOTAL_TLS_DISABLED`.
   *
   * ⚠️ THE ONLY ZONE-SCOPED ACTION IN THIS UNION, AND THE ONLY ONE THAT TAKES NO
   * `organizationId` AT ALL. Every other key repairs one tenant or one app, and
   * so needs a target off the issue row; this one changes a setting shared by
   * ALL of them, so its endpoint takes a reason and nothing else. It is the
   * third distinct entity convention in this union — cluster-scoped, app-scoped,
   * and now zone-scoped — which is why `ENTITY_ID_IS_ORGANIZATION_ID` in the
   * remedy modal is not enough on its own to decide what to pass: for this key
   * the answer is "nothing".
   *
   * The issue itself is still raised PER SERVING CLUSTER (there is no zone-scoped
   * entity in the Issue ledger; see `AssertVibeZoneTotalTlsUseCase`), so an
   * operator reaches this button from any one tenant's row while the repair it
   * runs is fleet-wide. One press clears every row on the next detector tick.
   */
  VIBE_ENABLE_ZONE_TOTAL_TLS: "vibe_enable_zone_total_tls",

  /**
   * Force ONE git project back to PENDING so the tenant agent re-materializes
   * its repository (NEX-3191) — the repair for
   * `VIBE_FORGEJO_REPOSITORY_MISSING`.
   *
   * 🔴 `entityId` HERE IS THE `VibeGitProject`'S OWN ID, NOT THE
   * ORGANIZATION'S — the code is `ISSUE_ENTITY_TYPE.VIBE_GIT_PROJECT`-scoped.
   * Read the target org off `issue.organizationId` and pass `issue.entityId` as
   * the project. Same shape as the two `VIBE_APP` keys above, and the same trap:
   * reading `entityId` as an org aims the repair at an id no organization has.
   *
   * ⚠️ ADMIN-ONLY, AND DELIBERATELY NOT THE TENANT ENDPOINT.
   * `POST /api/vibe/git-projects/:id/reprovision` accepts a FAILED project and
   * nothing else, enforced in its re-arm adapter's WHERE — a tenant has no
   * business forcing a healthy-looking project back to PENDING. This key routes
   * to a separate admin surface backed by a separate port whose WHERE names
   * `FAILED` and `READY`.
   *
   * A false positive is cheap: the agent's `ensureRepo` treats an existing
   * repository as idempotent success, so re-materializing a repo that is not
   * actually gone rotates the receive-hook token and costs nothing else. A TRUE
   * positive creates a FRESH EMPTY repository under the same name — it does NOT
   * recover prior history, which is why the code's runbook still says to confirm
   * the repository is really gone before pressing this.
   */
  VIBE_REPROVISION_GIT_PROJECT: "vibe_reprovision_git_project"
} as const;

export type IssueOperatorAction =
  (typeof ISSUE_OPERATOR_ACTION)[keyof typeof ISSUE_OPERATOR_ACTION];
